/**
 * Registro de formularios dinámicos custom por modelName (capa 2).
 * Los módulos registran componentes Vue que reemplazan FormV2 en add/edit/view.
 */

/** @type {Map<string, { add?: FormEntry, edit?: FormEntry, view?: FormEntry }>} */
const registry = new Map();

/**
 * @typedef {object} FormEntry
 * @property {() => Promise<{ default?: import('vue').Component }>|import('vue').Component} component
 * @property {string|null} [requiresModule]
 * @property {string|null} [customFormKey] - clave opcional desde meta del form-schema (p. ej. alisof:Contrato)
 * @property {boolean} [autonomous] - si true, add/edit delegan en DynamicModelCustomFormShell (sin AutoSaveForm ni onInput de edit/add)
 */

/**
 * @param {object} opts
 * @param {string} opts.modelName
 * @param {('add'|'edit'|'view')[]} [opts.modes]
 * @param {FormEntry['component']} opts.component
 * @param {string|null} [opts.requiresModule]
 * @param {string|null} [opts.customFormKey]
 * @param {boolean} [opts.autonomous]
 */
export function registerDynamicForm({
  modelName,
  modes = ["add", "edit", "view"],
  component,
  requiresModule = null,
  customFormKey = null,
  autonomous = false,
}) {
  if (!modelName || !component) {
    return;
  }
  const modelKey = String(modelName).toLowerCase();
  if (!registry.has(modelKey)) {
    registry.set(modelKey, {});
  }
  const entry = registry.get(modelKey);
  const payload = { component, requiresModule, customFormKey, autonomous: !!autonomous };
  for (const mode of modes) {
    if (mode === "add" || mode === "edit" || mode === "view") {
      entry[mode] = payload;
    }
  }
  if (customFormKey) {
    if (!registry.has(`key:${customFormKey}`)) {
      registry.set(`key:${customFormKey}`, {});
    }
    const keyEntry = registry.get(`key:${customFormKey}`);
    for (const mode of modes) {
      if (mode === "add" || mode === "edit" || mode === "view") {
        keyEntry[mode] = payload;
      }
    }
  }
}

function isModuleActive(moduleName, activeModules) {
  if (!moduleName) {
    return true;
  }
  const list = Array.isArray(activeModules) ? activeModules : [];
  return list.some((m) => {
    const name = typeof m === "string" ? m : m?.name;
    return name === moduleName;
  });
}

function getActiveModulesFromStorage() {
  try {
    const raw = localStorage.getItem("activeModules");
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

/**
 * @param {string} modelName
 * @param {'add'|'edit'|'view'} mode
 * @param {object} [options]
 * @param {string|null} [options.customFormKey] - desde form-schema meta
 * @param {Array} [options.activeModules]
 * @returns {Promise<import('vue').Component|null>}
 */
function resolveRegistration(modelName, mode, options = {}) {
  const customFormKey = options.customFormKey || null;
  let bucket = null;
  let resolvedViaCustomKey = false;
  if (customFormKey && registry.has(`key:${customFormKey}`)) {
    bucket = registry.get(`key:${customFormKey}`);
    if (bucket && bucket[mode]) {
      resolvedViaCustomKey = true;
    }
  }
  if (!bucket || !bucket[mode]) {
    const modelKey = String(modelName || "").toLowerCase();
    bucket = registry.get(modelKey);
  }
  if (!bucket || !bucket[mode]) {
    return null;
  }
  // form-schema solo emite customFormKey si el formalter (módulo activo) lo setea
  if (resolvedViaCustomKey && options.trustCustomFormKey) {
    return bucket[mode];
  }
  const activeModules =
    options.activeModules !== undefined
      ? options.activeModules
      : getActiveModulesFromStorage();
  const { requiresModule } = bucket[mode];
  if (!isModuleActive(requiresModule, activeModules)) {
    return null;
  }
  return bucket[mode];
}

/** @returns {{ component, requiresModule, customFormKey, autonomous }|null} */
export function getDynamicFormRegistration(modelName, mode, options = {}) {
  return resolveRegistration(modelName, mode, options);
}

export function isAutonomousDynamicForm(modelName, mode, options = {}) {
  const reg = resolveRegistration(modelName, mode, options);
  return !!(reg && reg.autonomous);
}

export async function resolveDynamicFormComponent(
  modelName,
  mode,
  options = {}
) {
  const reg = resolveRegistration(modelName, mode, options);
  if (!reg) {
    return null;
  }

  const { component } = reg;
  if (typeof component === "function") {
    const mod = await component();
    return mod.default || mod;
  }
  return component;
}

export function clearDynamicFormRegistry() {
  registry.clear();
}
